import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Flag, FlaskConical, Plus, Pencil, Trash2, Loader2, ToggleLeft, ToggleRight, X, Save, Play, Square, CheckCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminFeatureFlags = () => {
  const [tab, setTab] = useState('flags');
  const [flags, setFlags] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [showTestForm, setShowTestForm] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [editingTest, setEditingTest] = useState(null);

  const [flagForm, setFlagForm] = useState({ name: '', key: '', description: '', enabled: false, rollout_pct: 100, target_roles: [] });
  const [testForm, setTestForm] = useState({ name: '', key: '', description: '', status: 'draft', variants: [{ name: 'Control', weight: 50 }, { name: 'Variant A', weight: 50 }], target_pct: 100, success_metric: '' });

  const loadData = useCallback(async () => {
    try {
      const [flagsRes, testsRes] = await Promise.all([
        apiClient.request('/api/admin/features/flags'),
        apiClient.request('/api/admin/features/ab-tests'),
      ]);
      setFlags(flagsRes.flags || []);
      setAbTests(testsRes.tests || []);
    } catch (e) { console.error('Failed to load features:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const resetFlagForm = () => { setFlagForm({ name: '', key: '', description: '', enabled: false, rollout_pct: 100, target_roles: [] }); setEditingFlag(null); setShowFlagForm(false); };
  const resetTestForm = () => { setTestForm({ name: '', key: '', description: '', status: 'draft', variants: [{ name: 'Control', weight: 50 }, { name: 'Variant A', weight: 50 }], target_pct: 100, success_metric: '' }); setEditingTest(null); setShowTestForm(false); };

  const saveFlag = async () => {
    try {
      if (editingFlag) {
        await apiClient.request(`/api/admin/features/flags/${editingFlag}`, { method: 'PUT', body: JSON.stringify(flagForm) });
      } else {
        await apiClient.request('/api/admin/features/flags', { method: 'POST', body: JSON.stringify(flagForm) });
      }
      resetFlagForm();
      loadData();
    } catch (e) { console.error('Failed to save flag:', e); }
  };

  const saveTest = async () => {
    try {
      if (editingTest) {
        await apiClient.request(`/api/admin/features/ab-tests/${editingTest}`, { method: 'PUT', body: JSON.stringify(testForm) });
      } else {
        await apiClient.request('/api/admin/features/ab-tests', { method: 'POST', body: JSON.stringify(testForm) });
      }
      resetTestForm();
      loadData();
    } catch (e) { console.error('Failed to save test:', e); }
  };

  const toggleFlag = async (flag) => {
    try {
      await apiClient.request(`/api/admin/features/flags/${flag.key}`, {
        method: 'PUT',
        body: JSON.stringify({ ...flag, enabled: !flag.enabled }),
      });
      loadData();
    } catch (e) { console.error('Failed to toggle flag:', e); }
  };

  const deleteFlag = async (key) => {
    if (!window.confirm('Delete this feature flag?')) return;
    await apiClient.request(`/api/admin/features/flags/${key}`, { method: 'DELETE' });
    loadData();
  };

  const deleteTest = async (key) => {
    if (!window.confirm('Delete this A/B test?')) return;
    await apiClient.request(`/api/admin/features/ab-tests/${key}`, { method: 'DELETE' });
    loadData();
  };

  const editFlag = (flag) => {
    setFlagForm({ name: flag.name, key: flag.key, description: flag.description, enabled: flag.enabled, rollout_pct: flag.rollout_pct, target_roles: flag.target_roles || [] });
    setEditingFlag(flag.key);
    setShowFlagForm(true);
  };

  const editTest = (test) => {
    setTestForm({ name: test.name, key: test.key, description: test.description, status: test.status, variants: test.variants, target_pct: test.target_pct, success_metric: test.success_metric || '' });
    setEditingTest(test.key);
    setShowTestForm(true);
  };

  const ROLES = ['teen', 'parent', 'admin', 'school_admin', 'teacher'];

  if (loading) return <AdminLayout pageTitle="Feature Flags & A/B Tests">
      <CheatSheet pageId="features" /><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;

  return (
    <AdminLayout pageTitle="Feature Flags & A/B Tests">
      {/* Tab Switcher */}
      <div className="flex gap-1 mb-6 p-1 rounded-lg w-fit" style={{ backgroundColor: '#F0F0F0' }}>
        <button onClick={() => setTab('flags')} className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all" style={{ backgroundColor: tab === 'flags' ? 'white' : 'transparent', color: tab === 'flags' ? '#1A1209' : '#666', boxShadow: tab === 'flags' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }} data-testid="tab-flags">
          <Flag size={15} /> Feature Flags ({flags.length})
        </button>
        <button onClick={() => setTab('ab')} className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all" style={{ backgroundColor: tab === 'ab' ? 'white' : 'transparent', color: tab === 'ab' ? '#1A1209' : '#666', boxShadow: tab === 'ab' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }} data-testid="tab-ab-tests">
          <FlaskConical size={15} /> A/B Tests ({abTests.length})
        </button>
      </div>

      {/* ─── Feature Flags Tab ─────────────────────────────── */}
      {tab === 'flags' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: '#666' }}>Manage feature rollouts and kill-switches</p>
            <button onClick={() => { resetFlagForm(); setShowFlagForm(true); }} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#F5831F' }} data-testid="create-flag-btn">
              <Plus size={15} /> New Flag
            </button>
          </div>

          {flags.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #E0E0E0' }}>
              <Flag size={40} style={{ color: '#DDD' }} className="mx-auto mb-3" />
              <p className="text-sm font-medium" style={{ color: '#333' }}>No feature flags yet</p>
              <p className="text-xs" style={{ color: '#6B6257' }}>Create your first flag to control feature rollouts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {flags.map((flag) => (
                <div key={flag.key} className="bg-white rounded-lg p-4 flex items-center justify-between" style={{ border: '1px solid #E0E0E0' }} data-testid={`flag-${flag.key}`}>
                  <div className="flex items-center gap-4 flex-1">
                    <button onClick={() => toggleFlag(flag)} className="flex-shrink-0" data-testid={`toggle-flag-${flag.key}`}>
                      {flag.enabled ? <ToggleRight size={28} style={{ color: '#2EAA5C' }} /> : <ToggleLeft size={28} style={{ color: '#CCC' }} />}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{flag.name}</h4>
                        <code className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>{flag.key}</code>
                        {flag.rollout_pct < 100 && <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FFF7ED', color: '#C2410C' }}>{flag.rollout_pct}% rollout</span>}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: '#6B6257' }}>{flag.description || 'No description'}</p>
                      {flag.target_roles?.length > 0 && (
                        <div className="flex gap-1 mt-1">{flag.target_roles.map(r => <span key={r} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}>{r}</span>)}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => editFlag(flag)} className="p-2 rounded-lg hover:bg-gray-100"><Pencil size={14} style={{ color: '#666' }} /></button>
                    <button onClick={() => deleteFlag(flag.key)} className="p-2 rounded-lg hover:bg-red-50"><Trash2 size={14} style={{ color: '#DC2626' }} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── A/B Tests Tab ─────────────────────────────────── */}
      {tab === 'ab' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: '#666' }}>Manage experiments and variant testing</p>
            <button onClick={() => { resetTestForm(); setShowTestForm(true); }} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#7B4FD8' }} data-testid="create-test-btn">
              <Plus size={15} /> New A/B Test
            </button>
          </div>

          {abTests.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center" style={{ border: '1px solid #E0E0E0' }}>
              <FlaskConical size={40} style={{ color: '#DDD' }} className="mx-auto mb-3" />
              <p className="text-sm font-medium" style={{ color: '#333' }}>No A/B tests yet</p>
              <p className="text-xs" style={{ color: '#6B6257' }}>Create your first experiment to test hypotheses</p>
            </div>
          ) : (
            <div className="space-y-3">
              {abTests.map((test) => {
                const statusColors = { draft: { bg: '#F0F0F0', text: '#666' }, running: { bg: '#ECFDF5', text: '#059669' }, completed: { bg: '#EFF6FF', text: '#2563EB' } };
                const sc = statusColors[test.status] || statusColors.draft;
                const StatusIcon = test.status === 'running' ? Play : test.status === 'completed' ? CheckCircle : Square;
                return (
                  <div key={test.key} className="bg-white rounded-lg p-4" style={{ border: '1px solid #E0E0E0' }} data-testid={`test-${test.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{test.name}</h4>
                        <code className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>{test.key}</code>
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: sc.bg, color: sc.text }}>
                          <StatusIcon size={10} /> {test.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => editTest(test)} className="p-2 rounded-lg hover:bg-gray-100"><Pencil size={14} style={{ color: '#666' }} /></button>
                        <button onClick={() => deleteTest(test.key)} className="p-2 rounded-lg hover:bg-red-50"><Trash2 size={14} style={{ color: '#DC2626' }} /></button>
                      </div>
                    </div>
                    <p className="text-xs mb-3" style={{ color: '#6B6257' }}>{test.description || 'No description'}</p>
                    <div className="flex gap-2">
                      {(test.variants || []).map((v, i) => (
                        <div key={i} className="flex-1 p-2.5 rounded-lg text-center" style={{ backgroundColor: i === 0 ? '#F9F9F9' : '#F5F0FF' }}>
                          <p className="text-xs font-medium" style={{ color: '#333' }}>{v.name}</p>
                          <p className="text-lg font-bold" style={{ color: i === 0 ? '#666' : '#7B4FD8' }}>{v.weight}%</p>
                        </div>
                      ))}
                    </div>
                    {test.success_metric && <p className="text-xs mt-2" style={{ color: '#6B6257' }}>Success metric: {test.success_metric}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Flag Form Modal ───────────────────────────────── */}
      {showFlagForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={resetFlagForm}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="flag-form-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E0E0E0' }}>
              <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{editingFlag ? 'Edit' : 'Create'} Feature Flag</h3>
              <button onClick={resetFlagForm}><X size={18} style={{ color: '#666' }} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Name</label>
                <input value={flagForm.name} onChange={e => setFlagForm({ ...flagForm, name: e.target.value })} placeholder="Dark Mode" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="flag-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Key (unique identifier)</label>
                <input value={flagForm.key} onChange={e => setFlagForm({ ...flagForm, key: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="dark_mode" className="w-full px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: '#E0E0E0' }} disabled={!!editingFlag} data-testid="flag-key-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Description</label>
                <input value={flagForm.description} onChange={e => setFlagForm({ ...flagForm, description: e.target.value })} placeholder="Enable dark mode for users" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="flag-desc-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Status</label>
                  <button onClick={() => setFlagForm({ ...flagForm, enabled: !flagForm.enabled })} className="flex items-center gap-2 px-3 py-2 rounded-lg border w-full text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="flag-enabled-toggle">
                    {flagForm.enabled ? <ToggleRight size={20} style={{ color: '#2EAA5C' }} /> : <ToggleLeft size={20} style={{ color: '#CCC' }} />}
                    {flagForm.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Rollout %</label>
                  <input type="number" min={0} max={100} value={flagForm.rollout_pct} onChange={e => setFlagForm({ ...flagForm, rollout_pct: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="flag-rollout-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Target Roles</label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map(role => (
                    <button key={role} onClick={() => {
                      const roles = flagForm.target_roles.includes(role) ? flagForm.target_roles.filter(r => r !== role) : [...flagForm.target_roles, role];
                      setFlagForm({ ...flagForm, target_roles: roles });
                    }} className="px-3 py-1.5 rounded-full text-xs font-medium transition-all" style={{ backgroundColor: flagForm.target_roles.includes(role) ? '#F5831F' : '#F0F0F0', color: flagForm.target_roles.includes(role) ? 'white' : '#666' }}>
                      {role}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-1" style={{ color: '#6B6257' }}>Leave empty to target all roles</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t" style={{ borderColor: '#E0E0E0' }}>
              <button onClick={resetFlagForm} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: '#E0E0E0', color: '#666' }}>Cancel</button>
              <button onClick={saveFlag} disabled={!flagForm.name || !flagForm.key} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#F5831F' }} data-testid="flag-save-btn">
                <Save size={14} /> {editingFlag ? 'Update' : 'Create'} Flag
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── A/B Test Form Modal ───────────────────────────── */}
      {showTestForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={resetTestForm}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="test-form-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E0E0E0' }}>
              <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{editingTest ? 'Edit' : 'Create'} A/B Test</h3>
              <button onClick={resetTestForm}><X size={18} style={{ color: '#666' }} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Test Name</label>
                <input value={testForm.name} onChange={e => setTestForm({ ...testForm, name: e.target.value })} placeholder="Onboarding Flow v2" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="test-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Key</label>
                <input value={testForm.key} onChange={e => setTestForm({ ...testForm, key: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="onboarding_v2" className="w-full px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: '#E0E0E0' }} disabled={!!editingTest} data-testid="test-key-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Description</label>
                <input value={testForm.description} onChange={e => setTestForm({ ...testForm, description: e.target.value })} placeholder="Testing new onboarding flow" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="test-desc-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Status</label>
                  <select value={testForm.status} onChange={e => setTestForm({ ...testForm, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="test-status-select">
                    <option value="draft">Draft</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Target %</label>
                  <input type="number" min={1} max={100} value={testForm.target_pct} onChange={e => setTestForm({ ...testForm, target_pct: parseInt(e.target.value) || 100 })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="test-target-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#666' }}>Success Metric</label>
                <input value={testForm.success_metric} onChange={e => setTestForm({ ...testForm, success_metric: e.target.value })} placeholder="session_count_7d, challenge_completion_rate" className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="test-metric-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-2 block" style={{ color: '#666' }}>Variants</label>
                {testForm.variants.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input value={v.name} onChange={e => { const vs = [...testForm.variants]; vs[i] = { ...vs[i], name: e.target.value }; setTestForm({ ...testForm, variants: vs }); }} className="flex-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} placeholder="Variant name" />
                    <input type="number" value={v.weight} onChange={e => { const vs = [...testForm.variants]; vs[i] = { ...vs[i], weight: parseInt(e.target.value) || 0 }; setTestForm({ ...testForm, variants: vs }); }} className="w-20 px-3 py-2 rounded-lg border text-sm text-center" style={{ borderColor: '#E0E0E0' }} placeholder="%" />
                    {testForm.variants.length > 2 && (
                      <button onClick={() => setTestForm({ ...testForm, variants: testForm.variants.filter((_, j) => j !== i) })} className="p-1"><X size={14} style={{ color: '#DC2626' }} /></button>
                    )}
                  </div>
                ))}
                <button onClick={() => setTestForm({ ...testForm, variants: [...testForm.variants, { name: `Variant ${String.fromCharCode(64 + testForm.variants.length)}`, weight: 0 }] })} className="text-xs font-medium" style={{ color: '#7B4FD8' }}>+ Add Variant</button>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t" style={{ borderColor: '#E0E0E0' }}>
              <button onClick={resetTestForm} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: '#E0E0E0', color: '#666' }}>Cancel</button>
              <button onClick={saveTest} disabled={!testForm.name || !testForm.key} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#7B4FD8' }} data-testid="test-save-btn">
                <Save size={14} /> {editingTest ? 'Update' : 'Create'} Test
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFeatureFlags;
